import React, { useState, useEffect } from 'react';
import { Cloud, Sun, CloudRain, CloudSnow, CloudDrizzle, Wind, Droplets, Thermometer, Eye, CloudLightning, CloudFog } from 'lucide-react';

const LAT = 44.80;
const LON = -79.72;
const LOCATION = 'Port Severn, ON';

const WEATHER_ICONS = {
  0: { icon: Sun, label: 'Clear sky', color: 'text-yellow-400' },
  1: { icon: Sun, label: 'Mainly clear', color: 'text-yellow-400' },
  2: { icon: Cloud, label: 'Partly cloudy', color: 'text-slate-300' },
  3: { icon: Cloud, label: 'Overcast', color: 'text-slate-400' },
  45: { icon: CloudFog, label: 'Fog', color: 'text-slate-400' },
  48: { icon: CloudFog, label: 'Rime fog', color: 'text-slate-400' },
  51: { icon: CloudDrizzle, label: 'Light drizzle', color: 'text-blue-300' },
  53: { icon: CloudDrizzle, label: 'Drizzle', color: 'text-blue-400' },
  55: { icon: CloudDrizzle, label: 'Dense drizzle', color: 'text-blue-500' },
  61: { icon: CloudRain, label: 'Light rain', color: 'text-blue-400' },
  63: { icon: CloudRain, label: 'Rain', color: 'text-blue-500' },
  65: { icon: CloudRain, label: 'Heavy rain', color: 'text-blue-600' },
  71: { icon: CloudSnow, label: 'Light snow', color: 'text-cyan-300' },
  73: { icon: CloudSnow, label: 'Snow', color: 'text-cyan-400' },
  75: { icon: CloudSnow, label: 'Heavy snow', color: 'text-cyan-500' },
  80: { icon: CloudRain, label: 'Rain showers', color: 'text-blue-400' },
  81: { icon: CloudRain, label: 'Moderate showers', color: 'text-blue-500' },
  82: { icon: CloudRain, label: 'Heavy showers', color: 'text-blue-600' },
  85: { icon: CloudSnow, label: 'Snow showers', color: 'text-cyan-400' },
  86: { icon: CloudSnow, label: 'Heavy snow showers', color: 'text-cyan-500' },
  95: { icon: CloudLightning, label: 'Thunderstorm', color: 'text-purple-400' },
  96: { icon: CloudLightning, label: 'Thunderstorm + hail', color: 'text-purple-500' },
  99: { icon: CloudLightning, label: 'Severe thunderstorm', color: 'text-purple-600' },
};

const getWeatherInfo = (code) => WEATHER_ICONS[code] || { icon: Cloud, label: 'Unknown', color: 'text-slate-400' };

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export default function WeatherForecast() {
  const [current, setCurrent] = useState(null);
  const [daily, setDaily] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchWeather = async () => {
      try {
        const res = await fetch(
          `https://api.open-meteo.com/v1/forecast?latitude=${LAT}&longitude=${LON}&current=temperature_2m,apparent_temperature,weather_code,wind_speed_10m,relative_humidity_2m&daily=weather_code,temperature_2m_max,temperature_2m_min,precipitation_probability_max&timezone=America/Toronto&forecast_days=7&temperature_unit=celsius&wind_speed_unit=kmh`
        );
        const data = await res.json();
        setCurrent(data.current);
        setDaily(data.daily);
      } catch (err) {
        console.error('Failed to fetch weather:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchWeather();
  }, []);

  if (loading) {
    return (
      <div className="bg-gradient-to-br from-blue-600 to-blue-800 rounded-xl p-4 text-white animate-pulse">
        <div className="h-4 bg-white/20 rounded w-32 mb-3" />
        <div className="h-8 bg-white/20 rounded w-20 mb-2" />
        <div className="h-3 bg-white/20 rounded w-24" />
      </div>
    );
  }

  if (!current || !daily) {
    return (
      <div className="bg-gradient-to-br from-blue-600 to-blue-800 rounded-xl p-4 text-white">
        <p className="text-sm">Weather unavailable</p>
      </div>
    );
  }

  const weatherInfo = getWeatherInfo(current.weather_code);
  const WeatherIcon = weatherInfo.icon;

  return (
    <div className="bg-gradient-to-br from-blue-600 to-indigo-800 rounded-xl overflow-hidden" data-testid="weather-forecast">
      {/* Current Weather */}
      <div className="p-4">
        <div className="flex items-center justify-between mb-1">
          <p className="text-xs text-blue-200 font-medium">{LOCATION}</p>
          <p className="text-xs text-blue-200">Now</p>
        </div>
        <div className="flex items-center gap-3">
          <WeatherIcon className={`w-10 h-10 ${weatherInfo.color}`} />
          <div>
            <p className="text-3xl font-bold text-white">{Math.round(current.temperature_2m)}°C</p>
            <p className="text-xs text-blue-200">{weatherInfo.label}</p>
          </div>
        </div>
        <div className="flex gap-4 mt-3 text-xs text-blue-200">
          <span className="flex items-center gap-1">
            <Thermometer className="w-3 h-3" /> Feels {Math.round(current.apparent_temperature)}°
          </span>
          <span className="flex items-center gap-1">
            <Wind className="w-3 h-3" /> {Math.round(current.wind_speed_10m)} km/h
          </span>
          <span className="flex items-center gap-1">
            <Droplets className="w-3 h-3" /> {current.relative_humidity_2m}%
          </span>
        </div>
      </div>

      {/* 7-Day Forecast */}
      <div className="bg-black/20 px-4 py-3">
        <p className="text-xs text-blue-300 font-semibold mb-2">7-Day Forecast</p>
        <div className="flex gap-1 overflow-x-auto">
          {daily.time.map((date, i) => {
            const dayInfo = getWeatherInfo(daily.weather_code[i]);
            const DayIcon = dayInfo.icon;
            const d = new Date(date + 'T12:00:00');
            const dayName = i === 0 ? 'Today' : DAY_NAMES[d.getDay()];
            return (
              <div key={date} className="flex flex-col items-center min-w-[48px] py-1">
                <p className="text-xs text-blue-200 font-medium">{dayName}</p>
                <DayIcon className={`w-5 h-5 my-1 ${dayInfo.color}`} />
                <p className="text-xs text-white font-bold">{Math.round(daily.temperature_2m_max[i])}°</p>
                <p className="text-xs text-blue-300">{Math.round(daily.temperature_2m_min[i])}°</p>
                {daily.precipitation_probability_max[i] > 20 && (
                  <p className="text-[10px] text-blue-300 flex items-center gap-0.5">
                    <Droplets className="w-2 h-2" />{daily.precipitation_probability_max[i]}%
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
